import React from "react";
import { Box, Button, FormControlLabel, Switch, Typography } from "@mui/material";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { formatDate } from "../../common/constant/dates";
import { formatMoney } from "../../common/utils/format";
import { PILOT_EXPLAINER } from "../../common/utils/bringBuyers";
import { BringBuyersSeller, getBringBuyersSeller, setBringBuyers } from "../../common/requests/bringBuyers/endpoints";
import { Banner, DetailList, DetailSection, ErrorState, LoadingRows, errorMessage, useResource, useToast } from "../../ui";
import { buyersText, vouchersText } from "./bringBuyersData";

interface BringBuyersSectionProps {
  userId: string;
  name: string;
  /** Called after the switch changed something, so the pilot list can reload */
  onChanged?: () => void;
}

/** The seller's Bring Your Buyers switch and, when on, their code and numbers.
 * Turning it on is immediate; turning it off asks first, in place. */
export default function BringBuyersSection({ userId, name, onChanged }: BringBuyersSectionProps) {
  const { idToken } = useAuth();
  const toast = useToast();
  const state = useResource(async () => (await getBringBuyersSeller(userId, idToken!)).data, [userId, idToken], {
    enabled: !!idToken,
    errorText: "Couldn't load Bring Your Buyers.",
  });
  const [confirmOff, setConfirmOff] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const save = async (enabled: boolean) => {
    if (!idToken) return;
    setBusy(true);
    try {
      const { data } = await setBringBuyers(userId, enabled, idToken);
      state.setData(data);
      setConfirmOff(false);
      toast(enabled ? `Bring Your Buyers is on for ${name}` : `Bring Your Buyers is off for ${name}`);
      if (data.changed) onChanged?.();
    } catch (err) {
      toast(errorMessage(err, "Couldn't change Bring Your Buyers."), "error");
    } finally {
      setBusy(false);
    }
  };

  const data = state.data;
  return (
    <DetailSection title="Bring Your Buyers">
      {state.status === "error" && !data ? (
        <ErrorState message={state.error} onRetry={state.reload} />
      ) : !data ? (
        <LoadingRows rows={2} />
      ) : !data.available ? (
        <Typography variant="body2" sx={{ color: C.muted }}>
          Not installed on this database yet (marketplace migration 018).
        </Typography>
      ) : (
        <Body
          data={data}
          name={name}
          busy={busy}
          confirmOff={confirmOff}
          onToggle={(on) => (on ? save(true) : setConfirmOff(true))}
          onCancel={() => setConfirmOff(false)}
          onConfirmOff={() => save(false)}
        />
      )}
    </DetailSection>
  );
}

function Body({
  data,
  name,
  busy,
  confirmOff,
  onToggle,
  onCancel,
  onConfirmOff,
}: {
  data: BringBuyersSeller;
  name: string;
  busy: boolean;
  confirmOff: boolean;
  onToggle: (on: boolean) => void;
  onCancel: () => void;
  onConfirmOff: () => void;
}) {
  const canTurnOn = data.is_seller;
  const stats = data.stats;
  const currency = stats?.currency || "SGD";
  return (
    <>
      <Typography variant="body2" sx={{ color: C.ink2 }}>
        {PILOT_EXPLAINER}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", minHeight: 44 }}>
        <FormControlLabel
          sx={{ m: 0, minHeight: 44, gap: 0.5 }}
          control={
            <Switch
              checked={data.enabled || confirmOff}
              disabled={busy || confirmOff || (!data.enabled && !canTurnOn)}
              onChange={(e) => onToggle(e.target.checked)}
              inputProps={{ "aria-label": "Bring Your Buyers" }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {busy ? "Saving…" : data.enabled ? "On" : "Off"}
            </Typography>
          }
        />
        {data.enabled && data.enabled_at && (
          <Typography variant="caption" sx={{ color: C.muted }}>
            On since {formatDate(data.enabled_at)}
          </Typography>
        )}
        {!data.enabled && !canTurnOn && (
          <Typography variant="caption" sx={{ color: C.muted }}>
            Only active sellers can join.
          </Typography>
        )}
      </Box>

      {confirmOff && (
        <Banner
          tone="warning"
          title="Turn off Bring Your Buyers?"
          action={
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button onClick={onCancel} disabled={busy} sx={{ minHeight: 44 }}>
                Keep on
              </Button>
              <Button variant="outlined" color="error" onClick={onConfirmOff} disabled={busy} sx={{ minHeight: 44 }}>
                {busy ? "Turning off…" : "Turn off"}
              </Button>
            </Box>
          }
        >
          Buyers already linked to {name} stay linked, but their later orders pay the normal fee and get no welcome voucher. The link and code stop bringing in
          new buyers.
        </Banner>
      )}

      {data.enabled && (
        <DetailList
          items={[
            {
              label: "Code",
              value: data.code ? (
                <Box component="span" className="mono" sx={{ fontWeight: 700 }}>
                  {data.code}
                </Box>
              ) : (
                <Box component="span" sx={{ color: C.muted }}>
                  Not set
                </Box>
              ),
            },
            { label: "Buyers brought", value: stats ? buyersText(stats) : "–" },
            { label: "Orders at 0%", value: stats ? String(stats.orders) : "–" },
            { label: "Commission saved", value: stats ? formatMoney(stats.fees_saved, currency) : "–" },
            { label: "Vouchers paid", value: stats ? vouchersText(stats) : "–", full: true },
          ]}
        />
      )}
    </>
  );
}
